import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate } from "react-router-dom";
import {
  Button,
  ConfigProvider,
  message,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";

import { MakeApiCall } from "../../../apis";
import { primaryColor } from "../../../config";
import { isSwitchedFromSuperAdminSession } from "../../../utils/auth";
import { PageWrap } from "./style";
import { useThemeMode } from "../../../contexts/ThemeContext";
import { getLocalAntdTheme } from "../../../theme/antdTheme";

const TAB_CENTRAL = "central-logs";
const TAB_USER = "user-system-logs";

const MAX_PREVIEW = 100;

const formatDateTime = (unix) => {
  const ts = parseInt(unix, 10);
  if (!ts) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  }).format(new Date(ts * 1000));
};

const TruncatedText = ({ text, max = MAX_PREVIEW }) => {
  const s = text == null || text === "" ? "—" : String(text).replace(/\r/g, "");
  if (s === "—") return <span className="text-muted">—</span>;
  if (s.length <= max) {
    return (
      <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{s}</span>
    );
  }
  const preview = `${s.slice(0, max)}…`;
  return (
    <Tooltip
      title={
        <pre
          style={{
            maxWidth: 560,
            maxHeight: 320,
            overflow: "auto",
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 12,
          }}
        >
          {s}
        </pre>
      }
    >
      <span style={{ cursor: "help", wordBreak: "break-word" }}>{preview}</span>
    </Tooltip>
  );
};

const eventTypeColor = (type) => {
  const t = String(type || "").toLowerCase();
  if (t.includes("error")) return "error";
  if (t.includes("warn")) return "warning";
  if (t.includes("log") || t.includes("info")) return "processing";
  return "default";
};

const eventStatusColor = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "DONE" || s.includes("DONE_WITH")) return "success";
  if (s === "FAILED" || s.includes("FAIL")) return "error";
  if (s === "IN_PROGRESS" || s === "PENDING") return "processing";
  if (s.includes("TIMEOUT")) return "warning";
  return "default";
};

const prettyEventDetails = (raw) => {
  if (raw == null || raw === "") return "—";
  const str = String(raw);
  try {
    return JSON.stringify(JSON.parse(str), null, 2);
  } catch {
    return str;
  }
};

/** API returns { key: label }; use key as query value (matches sample URLs). */
const mapToSelectOptions = (map) =>
  Object.entries(map || {}).map(([value, label]) => ({
    value,
    label: label != null ? String(label) : value,
  }));

const tableComponents = {
  table: (props) => (
    <table
      className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7"
      {...props}
    />
  ),
  header: {
    wrapper: (props) => <thead {...props} />,
    row: (props) => <tr {...props} className="fw-bolder system-logs-table-header-row bg-cus" />,
    cell: (props) => <th {...props} />,
  },
};

export default function SystemLogs({ userData }) {
  const { themeMode } = useThemeMode();
  const [activeTab, setActiveTab] = useState(TAB_CENTRAL);

  const [centralList, setCentralList] = useState([]);
  const [centralLoading, setCentralLoading] = useState(true);
  const [centralPage, setCentralPage] = useState(1);
  const [centralPageSize, setCentralPageSize] = useState(20);
  const [centralTotal, setCentralTotal] = useState(0);

  const [centralFilters, setCentralFilters] = useState({
    marketplace: undefined,
    event_name: undefined,
    event_type: undefined,
    event_status: undefined,
  });
  const [filterLists, setFilterLists] = useState({
    marketplace_list: {},
    event_name_list: {},
    event_type_list: {},
    event_status_list: {},
  });
  const [filtersLoading, setFiltersLoading] = useState(false);
  const filtersFetchedRef = useRef(false);

  const [userList, setUserList] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(20);
  const [userTotal, setUserTotal] = useState(0);

  const [detailUser, setDetailUser] = useState(null);
  const [detailCentral, setDetailCentral] = useState(null);

  const canAccessSystemLogs =
    userData?.loginType === "user" &&
    isSwitchedFromSuperAdminSession(userData);

  useEffect(() => {
    filtersFetchedRef.current = false;
  }, [userData?.token]);

  const fetchCentralLogs = useCallback(async () => {
    setCentralLoading(true);
    const qs = new URLSearchParams();
    qs.set("page", String(centralPage));
    qs.set("per-page", String(centralPageSize));
    if (centralFilters.marketplace) {
      qs.set("marketplace", centralFilters.marketplace);
    }
    if (centralFilters.event_name) {
      qs.set("event_name", centralFilters.event_name);
    }
    if (centralFilters.event_type) {
      qs.set("event_type", centralFilters.event_type);
    }
    if (centralFilters.event_status) {
      qs.set("event_status", centralFilters.event_status);
    }
    const url = `application-logs/central-logs?${qs.toString()}`;
    const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);

    if (response?.status === true && response?.data) {
      setCentralList(response.data.records || []);
      const p = response.data.pagination || {};
      setCentralTotal(p.totalCount ?? 0);
      if (typeof p.page === "number") setCentralPage(p.page);
      if (typeof p.page_size === "number") setCentralPageSize(p.page_size);
    } else {
      setCentralList([]);
      setCentralTotal(0);
      if (response?.message) message.warning(response.message);
    }
    setCentralLoading(false);
  }, [centralPage, centralPageSize, centralFilters, userData?.token]);

  const fetchCentralFilterLists = useCallback(async () => {
    if (filtersFetchedRef.current) return;
    setFiltersLoading(true);
    const response = await MakeApiCall(
      "application-logs/filters",
      "get",
      null,
      true,
      {},
      userData?.token
    );
    if (response?.status === true && response?.data) {
      setFilterLists({
        marketplace_list: response.data.marketplace_list || {},
        event_name_list: response.data.event_name_list || {},
        event_type_list: response.data.event_type_list || {},
        event_status_list: response.data.event_status_list || {},
      });
      filtersFetchedRef.current = true;
    } else if (response?.message) {
      message.warning(response.message);
    }
    setFiltersLoading(false);
  }, [userData?.token]);

  const fetchUserSystemLogs = useCallback(async () => {
    setUserLoading(true);
    const url = `application-logs/user-system-logs?page=${userPage}&per-page=${userPageSize}`;
    const response = await MakeApiCall(url, "get", null, true, {}, userData?.token);

    if (response?.status === true && response?.data) {
      setUserList(response.data.records || []);
      const p = response.data.pagination || {};
      setUserTotal(p.totalCount ?? 0);
      if (typeof p.page === "number") setUserPage(p.page);
      if (typeof p.page_size === "number") setUserPageSize(p.page_size);
    } else {
      setUserList([]);
      setUserTotal(0);
      if (response?.message) message.warning(response.message);
    }
    setUserLoading(false);
  }, [userPage, userPageSize, userData?.token]);

  useEffect(() => {
    if (!canAccessSystemLogs || activeTab !== TAB_CENTRAL) return;
    fetchCentralLogs();
  }, [activeTab, canAccessSystemLogs, fetchCentralLogs]);

  useEffect(() => {
    if (!canAccessSystemLogs || activeTab !== TAB_CENTRAL) return;
    fetchCentralFilterLists();
  }, [activeTab, canAccessSystemLogs, fetchCentralFilterLists]);

  useEffect(() => {
    if (!canAccessSystemLogs || activeTab !== TAB_USER) return;
    fetchUserSystemLogs();
  }, [activeTab, canAccessSystemLogs, fetchUserSystemLogs]);

  if (!canAccessSystemLogs) {
    return <Navigate to="/dashboard" replace />;
  }

  const userColumns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 72,
    },
    {
      title: "Event name",
      dataIndex: "event_name",
      key: "event_name",
      width: 280,
      render: (text) => <TruncatedText text={text} max={120} />,
    },
    {
      title: "Type",
      dataIndex: "event_type",
      key: "event_type",
      width: 140,
      render: (text) => <Tag color={eventTypeColor(text)}>{text || "—"}</Tag>,
    },
    {
      title: "Code",
      dataIndex: "error_code",
      key: "error_code",
      width: 88,
      render: (text) => <span className="text-nowrap">{text ?? "—"}</span>,
    },
    {
      title: "Error message",
      dataIndex: "error_message",
      key: "error_message",
      width: 320,
      render: (text) => <TruncatedText text={text} max={100} />,
    },
    {
      title: "IP",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 120,
      render: (text) => <TruncatedText text={text} max={40} />,
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      width: 200,
      render: (text) => <TruncatedText text={text} max={60} />,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 168,
      render: (ts) => formatDateTime(ts),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setDetailUser(record)}>
          Details
        </Button>
      ),
    },
  ];

  const centralColumns = [
    { title: "ID", dataIndex: "id", key: "id", width: 80 },
    {
      title: "Scheduler",
      dataIndex: "user_data_scheduler_id",
      key: "user_data_scheduler_id",
      width: 96,
    },
    {
      title: "Event date",
      dataIndex: "event_date",
      key: "event_date",
      width: 112,
    },
    {
      title: "Event name",
      dataIndex: "event_name",
      key: "event_name",
      width: 260,
      render: (t) => <TruncatedText text={t} max={80} />,
    },
    {
      title: "Type",
      dataIndex: "event_type",
      key: "event_type",
      width: 140,
      render: (text) => <Tag color={eventTypeColor(text)}>{text || "—"}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "event_status",
      key: "event_status",
      width: 140,
      render: (text) => (
        <Tag color={eventStatusColor(text)}>{text || "—"}</Tag>
      ),
    },
    {
      title: "Marketplace",
      dataIndex: "marketplace",
      key: "marketplace",
      width: 120,
    },
    {
      title: "Marketplace ID",
      dataIndex: "marketplace_id",
      key: "marketplace_id",
      width: 140,
      render: (t) => <TruncatedText text={t} max={24} />,
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 100,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 80,
    },
    {
      title: "Retries",
      dataIndex: "retry_count",
      key: "retry_count",
      width: 80,
    },
    {
      title: "Row status",
      dataIndex: "status",
      key: "status",
      width: 96,
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      width: 168,
      render: (ts) => formatDateTime(ts),
    },
    {
      title: "Updated",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 168,
      render: (ts) => formatDateTime(ts),
    },
    {
      title: "",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => setDetailCentral(record)}>
          Details
        </Button>
      ),
    },
  ];

  const theme = getLocalAntdTheme(themeMode, primaryColor);

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const setCentralFilter = (field, value) => {
    setCentralPage(1);
    setCentralFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearCentralFilters = () => {
    setCentralPage(1);
    setCentralFilters({
      marketplace: undefined,
      event_name: undefined,
      event_type: undefined,
      event_status: undefined,
    });
  };

  const marketplaceOptions = mapToSelectOptions(filterLists.marketplace_list);
  const eventNameOptions = mapToSelectOptions(filterLists.event_name_list);
  const eventTypeOptions = mapToSelectOptions(filterLists.event_type_list);
  const eventStatusOptions = mapToSelectOptions(filterLists.event_status_list);

  const centralFilterBar = (
    <div className="system-logs-filter system-logs-filter-wrap mb-10 mx-md-n9 mx-n2 px-4 px-md-10 py-5 filter">
      <div className="d-flex flex-wrap align-items-end gap-4 gap-lg-6 justify-content-between">
        <Space wrap size={[16, 16]} className="flex-grow-1">
          <div className="d-flex flex-column" style={{ minWidth: 160 }}>
            <span className="fs-7 fw-bold system-logs-filter-label mb-2">Marketplace</span>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              variant="filled"
              placeholder="All"
              loading={filtersLoading}
              value={centralFilters.marketplace}
              onChange={(v) => setCentralFilter("marketplace", v ?? undefined)}
              options={marketplaceOptions}
              style={{ minWidth: 180 }}
              size="large"
              className="fs-7"
            />
          </div>
          <div className="d-flex flex-column" style={{ minWidth: 160 }}>
            <span className="fs-7 fw-bold system-logs-filter-label mb-2">Event name</span>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              variant="filled"
              placeholder="All"
              loading={filtersLoading}
              value={centralFilters.event_name}
              onChange={(v) => setCentralFilter("event_name", v ?? undefined)}
              options={eventNameOptions}
              style={{ minWidth: 260 }}
              size="large"
              className="fs-7"
            />
          </div>
          <div className="d-flex flex-column" style={{ minWidth: 160 }}>
            <span className="fs-7 fw-bold system-logs-filter-label mb-2">Event type</span>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              variant="filled"
              placeholder="All"
              loading={filtersLoading}
              value={centralFilters.event_type}
              onChange={(v) => setCentralFilter("event_type", v ?? undefined)}
              options={eventTypeOptions}
              style={{ minWidth: 200 }}
              size="large"
              className="fs-7"
            />
          </div>
          <div className="d-flex flex-column" style={{ minWidth: 160 }}>
            <span className="fs-7 fw-bold system-logs-filter-label mb-2">Event status</span>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              variant="filled"
              placeholder="All"
              loading={filtersLoading}
              value={centralFilters.event_status}
              onChange={(v) => setCentralFilter("event_status", v ?? undefined)}
              options={eventStatusOptions}
              style={{ minWidth: 180 }}
              size="large"
              className="fs-7"
            />
          </div>
        </Space>
        <button
          type="button"
          className="btn btn-sm btn-light fw-bold fs-7 system-logs-clear-btn"
          onClick={clearCentralFilters}
        >
          Clear filters
        </button>
      </div>
    </div>
  );

  const tabItems = [
    {
      key: TAB_CENTRAL,
      label: "Central logs",
      children: (
        <>
          {centralFilterBar}
          <>
            <div className="table-responsive">
              <Table
                scroll={{ x: "max-content" }}
                columns={centralColumns}
                loading={centralLoading}
                dataSource={centralList}
                rowKey="id"
                pagination={false}
                components={tableComponents}
              />
            </div>
            <div className="card-body py-2 pt-4">
              <Pagination
                className="mt-3"
                current={centralPage}
                pageSize={centralPageSize}
                total={centralTotal}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(currentPage, newPageSize) => {
                  if (newPageSize !== centralPageSize) {
                    setCentralPage(1);
                    setCentralPageSize(newPageSize);
                  } else {
                    setCentralPage(currentPage);
                  }
                }}
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} records`
                }
              />
            </div>
          </>
        </>
      ),
    },
    {
      key: TAB_USER,
      label: "User system logs",
      children: (
        <>
          <div className="table-responsive">
            <Table
              scroll={{ x: "max-content" }}
              columns={userColumns}
              loading={userLoading}
              dataSource={userList}
              rowKey="id"
              pagination={false}
              components={tableComponents}
            />
          </div>
          <div className="card-body py-2 pt-4">
            <Pagination
              className="mt-3"
              current={userPage}
              pageSize={userPageSize}
              total={userTotal}
              showSizeChanger
              pageSizeOptions={["10", "20", "50", "100"]}
              onChange={(currentPage, newPageSize) => {
                if (newPageSize !== userPageSize) {
                  setUserPage(1);
                  setUserPageSize(newPageSize);
                } else {
                  setUserPage(currentPage);
                }
              }}
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total} records`
              }
            />
          </div>
        </>
      ),
    },
  ];

  return (
    <ConfigProvider theme={theme}>
      <PageWrap>
        <Helmet>
          <title>System Logs</title>
        </Helmet>

        <div className="row gy-5 g-xl-5 mt-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-header border-bottom-dashed flex-wrap gap-2">
                <div>
                  <h3 className="card-title fw-bolder mb-1">System Logs</h3>
                  <span className="text-muted fs-7">
                    Central scheduler events and per-user application errors
                  </span>
                </div>
              </div>
              <div className="card-body py-3">
                <Tabs
                  className="system-logs-tabs"
                  activeKey={activeTab}
                  onChange={handleTabChange}
                  destroyInactiveTabPane
                  items={tabItems}
                />
              </div>
            </div>
          </div>
        </div>

        <Modal
          title={
            detailUser
              ? `Log #${detailUser.id} — ${detailUser.event_type || "Event"}`
              : "Log details"
          }
          open={!!detailUser}
          onCancel={() => setDetailUser(null)}
          centered
          footer={
            <Button type="primary" onClick={() => setDetailUser(null)}>
              Close
            </Button>
          }
          width={720}
        >
          {detailUser && (
            <div className="fs-7" style={{ maxHeight: "70vh", overflow: "auto" }}>
              <p className="fw-bold system-logs-modal-heading mb-2">Event name</p>
              <pre className="system-logs-modal-pre">
                {detailUser.event_name || "—"}
              </pre>
              <p className="fw-bold system-logs-modal-heading mb-2">Error message</p>
              <pre className="system-logs-modal-pre">
                {detailUser.error_message || "—"}
              </pre>
              <p className="fw-bold system-logs-modal-heading mb-2">Error data</p>
              <pre className="system-logs-modal-pre">
                {detailUser.error_data || "—"}
              </pre>
              <p className="text-muted fs-8 mb-0">
                IP: {detailUser.ip_address || "—"} · URL: {detailUser.url || "—"} ·
                Created: {formatDateTime(detailUser.created_at)}
              </p>
            </div>
          )}
        </Modal>

        <Modal
          title={
            detailCentral
              ? `Central #${detailCentral.id} — ${detailCentral.event_name || "Event"}`
              : "Central log details"
          }
          open={!!detailCentral}
          onCancel={() => setDetailCentral(null)}
          centered
          footer={
            <Button type="primary" onClick={() => setDetailCentral(null)}>
              Close
            </Button>
          }
          width={800}
        >
          {detailCentral && (
            <div className="fs-7" style={{ maxHeight: "70vh", overflow: "auto" }}>
              <p className="mb-3">
                <Tag color={eventStatusColor(detailCentral.event_status)}>
                  {detailCentral.event_status || "—"}
                </Tag>{" "}
                <Tag color={eventTypeColor(detailCentral.event_type)}>
                  {detailCentral.event_type || "—"}
                </Tag>
              </p>
              <p className="fw-bold system-logs-modal-heading mb-2">Event details (JSON)</p>
              <pre className="system-logs-modal-pre">
                {prettyEventDetails(detailCentral.event_details)}
              </pre>
              {detailCentral.note ? (
                <>
                  <p className="fw-bold system-logs-modal-heading mb-2">Note</p>
                  <pre className="system-logs-modal-pre">{detailCentral.note}</pre>
                </>
              ) : null}
              <p className="text-muted fs-8 mb-0">
                Date: {detailCentral.event_date || "—"} · Marketplace:{" "}
                {detailCentral.marketplace || "—"} ({detailCentral.marketplace_id || "—"}
                ) · Region: {detailCentral.region || "—"} · Scheduler:{" "}
                {detailCentral.user_data_scheduler_id ?? "—"} · Created:{" "}
                {formatDateTime(detailCentral.created_at)} · Updated:{" "}
                {formatDateTime(detailCentral.updated_at)}
              </p>
            </div>
          )}
        </Modal>
      </PageWrap>
    </ConfigProvider>
  );
}
